"use strict";
/**
 * @typedef {'local' | 'session'} StorageType
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.storageWrapper = storageWrapper;
exports.setItem = setItem;
exports.getItem = getItem;
exports.removeItem = removeItem;
exports.clear = clear;
exports.isSupported = isSupported;
/**
 * @param {StorageType} [storageType]
 * @returns {Storage}
 */
function getStorageByType(storageType) {
    return storageType === 'local' ? window.localStorage : window.sessionStorage;
}
/**
 * Checks if the specified storage type is available
 * @param {StorageType} [storageType='local']
 * @returns {boolean}
 */
function isSupported(storageType = 'local') {
    if (typeof window === 'undefined')
        return false;
    try {
        const storage = getStorageByType(storageType);
        const testKey = '_storage_test_';
        storage.setItem(testKey, testKey);
        storage.getItem(testKey);
        storage.removeItem(testKey);
        return true;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Storage type ${storageType} is not supported.`, error);
        return false;
    }
}
/**
 * @param {string} key
 * @param {*} valueObject
 * @param {StorageType} [storageType]
 */
function setItem(key, valueObject, storageType = 'session') {
    try {
        const valueString = JSON.stringify(valueObject);
        getStorageByType(storageType).setItem(key, valueString);
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error setting item in ${storageType} storage.`, error);
    }
}
/**
 * @param {string} key
 * @param {StorageType} [storageType]
 * @returns {any | null}
 */
function getItem(key, storageType = 'session') {
    if (typeof window !== 'undefined') {
        try {
            const storageItem = getStorageByType(storageType).getItem(key);
            return storageItem ? JSON.parse(storageItem) : null;
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Error getting item from ${storageType} storage.`, error);
            return null;
        }
    }
    return null;
}
/**
 * @param {string} key
 * @param {StorageType} [storageType]
 */
function removeItem(key, storageType = 'session') {
    try {
        getStorageByType(storageType).removeItem(key);
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error removing item from ${storageType} storage.`, error);
    }
}
/**
 * @param {StorageType} [storageType]
 */
function clear(storageType) {
    if (typeof window !== 'undefined') {
        try {
            getStorageByType(storageType).clear();
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Error clearing ${storageType} storage.`, error);
        }
    }
}
/**
 * Wraps the base utilities to be able to have in-memory storage in cases the base API
 * is not available and it's needed
 */
function storageWrapper() {
    const session = {};
    const local = {};
    /**
     * @param {StorageType} [storageType]
     */
    function getStorage(storageType) {
        return storageType === 'local' ? local : session;
    }
    /**
     * @param {StorageType} [storageType]
     */
    function wrapClear(storageType) {
        if (isSupported(storageType)) {
            clear(storageType);
        }
    }
    /**
     * @param {string} key
     * @param {StorageType} [storageType]
     * @returns {* | null}
     */
    function wrapGetItem(key, storageType = 'session') {
        if (isSupported(storageType)) {
            return getItem(key, storageType);
        }
        const storage = getStorage(storageType);
        return storage[key] || null;
    }
    /**
     * @param {string} key
     * @param {*} valueObject
     * @param {StorageType} [storageType]
     */
    function wrapSetItem(key, valueObject, storageType = 'session') {
        if (isSupported(storageType)) {
            setItem(key, valueObject, storageType);
        }
        else {
            const storage = getStorage(storageType);
            storage[key] = valueObject;
        }
    }
    /**
     * @param {string} key
     * @param {StorageType} [storageType]
     */
    function wrapRemoveItem(key, storageType = 'session') {
        if (isSupported(storageType)) {
            removeItem(key, storageType);
        }
        else {
            const storage = getStorage(storageType);
            delete storage[key];
        }
    }
    return {
        getItem: wrapGetItem,
        setItem: wrapSetItem,
        removeItem: wrapRemoveItem,
        clear: wrapClear
    };
}
//# sourceMappingURL=storage.js.map